import { FC, useEffect, useState } from "react";
import { DateTime } from "luxon";
import { useFormik } from "formik";
import {
  Button,
  DatePicker,
  Divider,
  InputField,
  Slider,
} from "../../components";
import { useLoanCalculator } from "../../contexts";
import {
  PayableFinancingCalculatorFields,
  getPayableInvoicingValidationSchema,
} from "./service";
import { LoanDurationType } from "../../hooks/api/types";
import { roundTo, toTitleCase } from "../../utils";
import { useStepper } from "../LoanApplicationSteps/Stepper";
import { ArrowLeftIcon } from "../../assets/icons";
import { useLoanMetrics } from "../../hooks";

import "./InvoiceDiscountingLoan.css";
import TermsAndConditions from "./TermsAndConditions";
import { useDpConfig } from "../../contexts/dpConfig";

const PAGE_COPY: {
  [key: string]: {
    default: (...args: any[]) => string;
    repayableByDP: (...args: any[]) => string;
  };
} = {
  TITLE: {
    default: () => "Total repayment amount",
    repayableByDP: () => "Total payment amount",
  },
  SUBTITLE: {
    default: (interestRate: number) =>
      `Pay as low as ${interestRate}% monthly interest*.`,
    repayableByDP: (interestRate: number) =>
      `Pay from ${interestRate}% monthly fees*.`,
  },
  LOAN_AMOUNT: {
    default: () => "Loan amount",
    repayableByDP: () => "Invoice amount",
  },
  LOAN_DURATION: {
    default: () => "Loan duration",
    repayableByDP: () => "Invoice due date",
  },
};

function getCopyText(key: string, repayableByDPFlag?: boolean) {
  return PAGE_COPY[key][repayableByDPFlag ? "repayableByDP" : "default"];
}

type Props = {
  editMode?: boolean;
  handleSubmit?: () => void;
  handleCancel?: () => void;
};

const PayableFinancingLoanCalculator: FC<Props> = ({
  editMode = false,
  handleSubmit: handleSubmitFromParent,
  handleCancel: handleCancelFromParent,
}) => {
  const [isPartnerTermsAccepted, setIsPartnerTermsAccepted] =
    useState<boolean>(false);
  const {
    state: { payableFinancingLoan, constraints },
    actions: { setPayableFinancingLoan },
  } = useLoanCalculator();
  const {
    state: { dpTermsUrl },
  } = useDpConfig();
  const { nextStep } = useStepper();

  const {
    payableFinancingProduct: {
      flags: { repayableByDP },
    },
    totalPayableFinancingPaymentForSME,
  } = useLoanMetrics();

  const [currentInvoice] = payableFinancingLoan.invoices;

  const [invoiceDueDate, setInvoiceDueDate] = useState<Date | null>(
    repayableByDP
      ? currentInvoice?.invoiceDueDate ??
          DateTime.fromJSDate(new Date())
            .plus({
              days: constraints.payableFinancingLoan?.duration?.initial,
            })
            .toJSDate()
      : null
  );

  const { values, errors, setFieldValue, handleSubmit } =
    useFormik<PayableFinancingCalculatorFields>({
      enableReinitialize: true,
      initialValues: {
        loanAmount:
          currentInvoice && !isNaN(currentInvoice.amount)
            ? currentInvoice.amount
            : constraints.payableFinancingLoan?.amount?.initial,
        duration:
          currentInvoice && !isNaN(currentInvoice.duration)
            ? currentInvoice.duration
            : constraints.payableFinancingLoan?.duration?.initial,
      },
      validationSchema: getPayableInvoicingValidationSchema({
        loanAmount: {
          min: constraints.payableFinancingLoan?.amount?.min,
          max: constraints.payableFinancingLoan?.amount?.max,
          unit: constraints.payableFinancingLoan?.amount?.unit ?? "AED",
        },
        duration: {
          min: constraints.payableFinancingLoan?.duration?.min,
          max: constraints.payableFinancingLoan?.duration?.max,
        },
      }),
      onSubmit: () => {
        if (repayableByDP) {
          currentInvoice.invoiceDueDate = invoiceDueDate!;
        }
        setPayableFinancingLoan({
          invoices: [currentInvoice],
        });
        nextStep();
      },
    });

  useEffect(() => {
    if (!payableFinancingLoan.invoices?.length) {
      setPayableFinancingLoan({
        invoices: currentInvoice
          ? [currentInvoice]
          : [
              {
                amount: constraints.payableFinancingLoan?.amount?.initial,
                duration: constraints.payableFinancingLoan?.duration?.initial,
                discountRate: 100,
                interestRate: constraints.payableFinancingLoan.interestRate,
                invoiceId: "",
                durationType: constraints.payableFinancingLoan.duration
                  .unit as LoanDurationType,
              },
            ],
      });
    }
  }, [values, payableFinancingLoan.invoices?.length]);

  const handleInputChange = (name: string, value: string) => {
    const num = Number(value.replaceAll(",", ""));
    if (isNaN(num)) {
      return null;
    }

    setFieldValue(name, num, true);

    const validation: any = {
      loanAmount: constraints.payableFinancingLoan.amount,
      duration: constraints.payableFinancingLoan.duration,
      discountRate: constraints.payableFinancingLoan.discount,
    };

    if (num > validation[name].max || num < validation[name].min) {
      return null;
    }

    return num;
  };

  const handleLoanAmountInputChange = (value: string) => {
    const val = handleInputChange("loanAmount", value);
    if (val !== null) {
      currentInvoice.amount = val;
      currentInvoice.interestRate =
        constraints.payableFinancingLoan.interestRate;
      setPayableFinancingLoan({
        invoices: [currentInvoice],
      });
    }
  };

  const handleLoanAmountSliderChange = (value: number) => {
    setFieldValue("loanAmount", value);
    currentInvoice.amount = value;
    currentInvoice.interestRate = constraints.payableFinancingLoan.interestRate;
    setPayableFinancingLoan({
      invoices: [currentInvoice],
    });
  };

  const handleDurationInputChange = (value: string, date?: Date) => {
    const val = handleInputChange("duration", value);
    if (val !== null) {
      currentInvoice.duration = val;
      currentInvoice.interestRate =
        constraints.payableFinancingLoan.interestRate;
      if (date) {
        currentInvoice.invoiceDueDate = date;
      }
      setPayableFinancingLoan({
        invoices: [currentInvoice],
      });
    }
  };

  const handleDurationSliderChange = (value: number) => {
    setFieldValue("duration", value);
    currentInvoice.duration = value;
    currentInvoice.interestRate = constraints.payableFinancingLoan.interestRate;
    setPayableFinancingLoan({
      invoices: [currentInvoice],
    });
  };

  const handleTermsChange = (checked: boolean) => {
    setIsPartnerTermsAccepted(checked);
  };

  return (
    <div className="cx-w-full cx-flex cx-flex-col cx-p-10 cx-gap-10 cx-items-center cx-rounded-modal cx-border-0.5 cx-border-[#DDD] cx-shadow-modal cx-background-calculator-gradient">
      <div className="cx-flex cx-flex-col cx-items-center cx-w-full">
        <div className="cx-font-medium">
          {getCopyText("TITLE", repayableByDP)()}
        </div>
        <div className="cx-text-3xl cx-font-bold">
          {constraints.payableFinancingLoan?.amount?.unit}{" "}
          {totalPayableFinancingPaymentForSME &&
            roundTo(totalPayableFinancingPaymentForSME, 2).toLocaleString()}
        </div>
        <div className="cx-text-sm cx-text-text-secondary">
          {getCopyText(
            "SUBTITLE",
            repayableByDP
          )(roundTo((currentInvoice?.interestRate / 12) * 100, 2))}
        </div>
      </div>
      <Divider />
      <div className="cx-flex cx-flex-row cx-w-full cx-gap-6">
        <div className="cx-flex cx-flex-col cx-gap-4 cx-w-full">
          <div className="cx-font-semibold">
            {getCopyText("LOAN_AMOUNT", repayableByDP)()}
          </div>
          <div>
            <InputField
              inputProps={{
                name: "loanAmount",
                value: values.loanAmount?.toLocaleString(),
                onChange: (e) => handleLoanAmountInputChange(e.target.value),
              }}
              inputClassName="cx-text-right !cx-p-2 cx-font-bold"
              prepend={constraints.payableFinancingLoan?.amount?.unit}
              error={errors.loanAmount}
              hideErrorMessage
            />
          </div>
          {!repayableByDP && (
            <div className="">
              <Slider
                initial={values.loanAmount}
                min={constraints.payableFinancingLoan?.amount?.min}
                max={constraints.payableFinancingLoan?.amount?.max}
                unit={constraints.payableFinancingLoan?.amount?.unit}
                value={values.loanAmount}
                label=""
                onChange={(value) => handleLoanAmountSliderChange(value)}
                renderThumb={false}
                interval={1000}
              />
            </div>
          )}
          {errors.loanAmount && (
            <div className="cx-text-xs cx-py-1 cx-text-text-error">
              {errors.loanAmount}
            </div>
          )}
        </div>
        <div className="cx-flex cx-flex-col cx-gap-4 cx-w-full">
          <div className="cx-font-semibold">
            {getCopyText("LOAN_DURATION", repayableByDP)()}
          </div>
          <div>
            {repayableByDP ? (
              <DatePicker
                selectedDate={invoiceDueDate!}
                min={DateTime.fromJSDate(new Date())
                  .plus({
                    days: constraints.payableFinancingLoan?.duration?.min,
                  })
                  .toJSDate()}
                max={DateTime.fromJSDate(new Date())
                  .plus({
                    days: constraints.payableFinancingLoan?.duration?.max,
                  })
                  .toJSDate()}
                onChange={(date) => {
                  const now = DateTime.fromJSDate(new Date());
                  const invoiceDate = DateTime.fromJSDate(date);
                  const duration = invoiceDate.diff(now, "days").as("days");
                  setInvoiceDueDate(date);
                  handleDurationInputChange(
                    Math.ceil(duration).toString(),
                    date
                  );
                }}
                className="!cx-py-2 cx-font-bold"
              />
            ) : (
              <InputField
                inputProps={{
                  name: "duration",
                  value: values.duration,
                  onChange: (e) => handleDurationInputChange(e.target.value),
                }}
                inputClassName="cx-text-right !cx-p-2 cx-font-bold"
                prepend={toTitleCase(
                  constraints.payableFinancingLoan?.duration?.unit
                )}
              />
            )}
          </div>
          {!repayableByDP && (
            <div className="">
              <Slider
                initial={values.duration}
                min={constraints.payableFinancingLoan?.duration?.min}
                max={constraints.payableFinancingLoan?.duration?.max}
                unit={constraints.payableFinancingLoan?.duration?.unit}
                label=""
                onChange={(value) => handleDurationSliderChange(value)}
                renderThumb={false}
              />
            </div>
          )}
        </div>
      </div>
      {/* <Hint
        text={`${getCopyText(
          "LOAN_AMOUNT",
          repayableByDP
        )()} should be exclusive of VAT.`}
        rounded
        icon
        className="cx-w-full -cx-mt-6"
      /> */}
      <Divider />
      <div className="cx-w-[400px]">
        {editMode ? (
          <div className="cx-w-full cx-flex cx-flex-row cx-gap-x-4">
            <div className="cx-w-1/4">
              <Button
                label={<ArrowLeftIcon />}
                type="button"
                onClick={handleCancelFromParent}
                fullWidth
                outlined
              />
            </div>
            <div className="cx-w-3/4">
              <Button
                type="button"
                label="Confirm"
                fullWidth
                onClick={handleSubmitFromParent}
              />
            </div>
          </div>
        ) : (
          <Button
            label="Apply"
            arrow="right"
            fullWidth
            onClick={handleSubmit}
            disabled={!isPartnerTermsAccepted && !!dpTermsUrl}
          />
        )}
      </div>
      <Divider />
      <div className="cx-block">
        <TermsAndConditions onTermsChange={handleTermsChange} />
      </div>
    </div>
  );
};

export default PayableFinancingLoanCalculator;
